import { useEffect, useState } from "react";
import { FileSignature, Loader2, Play } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

/** One-off tidy-up: every picture the browser named for us ("image.png") gets
 *  the name of the article it sits in, so the library can be searched. */
export default function KbMediaNamesCard() {
  const [plan, setPlan] = useState(null);
  const [picked, setPicked] = useState({});
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/kb/media-names/plan");
      setPlan(data);
      setPicked(Object.fromEntries((data.proposals || []).map((p) => [p.asset_id, true])));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't work out the new names");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open && !plan) load(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const chosen = Object.entries(picked).filter(([, v]) => v).map(([k]) => k);

  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/kb/media-names/apply", { asset_ids: chosen });
      toast.success(`${data.renamed} file${data.renamed === 1 ? "" : "s"} renamed`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't rename those");
    } finally { setBusy(false); }
  };

  const rows = plan?.proposals || [];

  return (
    <Card className="px-5 py-4" data-testid="kb-media-names-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="flex items-center gap-2 font-heading text-base font-semibold text-slate-800 dark:text-slate-100">
            <FileSignature className="h-4 w-4 text-slate-400" /> Tidy up file names
          </h3>
          <p className="mt-1 max-w-2xl text-xs text-slate-500 dark:text-slate-400">
            A pasted screenshot arrives called “image.png”, so the media library fills with
            files nobody can find again. This renames each one after the article it sits in
            (<code>article-slug-3.png</code>). Pictures used in more than one article, and
            pictures used nowhere, are left alone.
          </p>
        </div>
        <Button size="sm" variant={open ? "outline" : "default"} onClick={() => setOpen(!open)}
          data-testid="kb-media-names-toggle">
          {open ? "Hide" : "Show what would change"}
        </Button>
      </div>

      {open && (
        <div className="mt-4 border-t border-slate-100 dark:border-slate-800 pt-3">
          {loading && !plan ? (
            <div className="flex h-24 items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-slate-300 dark:text-slate-600" />
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-xs text-slate-500 dark:text-slate-400" data-testid="kb-media-names-summary">
                  {rows.length} to rename
                  {plan?.skipped_shared ? `, ${plan.skipped_shared} skipped (used in several articles)` : ""}
                  {plan?.skipped_unused ? `, ${plan.skipped_unused} skipped (not used anywhere)` : ""}
                </span>
                {rows.length > 0 && (
                  <>
                    <button type="button" data-testid="kb-media-names-all"
                      onClick={() => setPicked(Object.fromEntries(rows.map((p) => [p.asset_id, true])))}
                      className="text-[11px] font-semibold text-primary hover:underline">Tick all</button>
                    <button type="button" data-testid="kb-media-names-none"
                      onClick={() => setPicked({})}
                      className="text-[11px] font-semibold text-slate-500 hover:underline">Untick all</button>
                    <Button size="sm" className="ml-auto" onClick={apply}
                      disabled={busy || !chosen.length} data-testid="kb-media-names-apply">
                      {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                        : <Play className="mr-1.5 h-3.5 w-3.5" />}
                      Rename {chosen.length}
                    </Button>
                  </>
                )}
              </div>

              {rows.length === 0 ? (
                <p className="mt-3 text-xs text-slate-500 dark:text-slate-400" data-testid="kb-media-names-empty">
                  Nothing to tidy — every picture in an article already has a name of its own.
                </p>
              ) : (
                <div className="mt-3 max-h-[360px] space-y-1 overflow-y-auto">
                  {rows.map((p) => (
                    <label key={p.asset_id} data-testid={`kb-media-names-row-${p.asset_id}`}
                      className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-100 dark:border-slate-800 px-2.5 py-1.5 hover:border-primary/40">
                      <input type="checkbox" checked={!!picked[p.asset_id]}
                        data-testid={`kb-media-names-tick-${p.asset_id}`}
                        onChange={(e) => setPicked((s) => ({ ...s, [p.asset_id]: e.target.checked }))}
                        className="h-3.5 w-3.5 accent-primary" />
                      <img src={p.url} alt="" className="h-8 w-12 shrink-0 rounded object-cover" />
                      <span className="min-w-0 flex-1 truncate font-mono text-[11px] text-slate-400 dark:text-slate-500">
                        {p.old}
                      </span>
                      <span className="shrink-0 text-[11px] text-slate-300">→</span>
                      <span className="min-w-0 flex-1 truncate font-mono text-[11px] font-semibold text-slate-700 dark:text-slate-200">
                        {p.new}
                      </span>
                      <span className="hidden min-w-0 max-w-[220px] shrink truncate text-[11px] text-slate-500 dark:text-slate-400 sm:block">
                        {p.article}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </Card>
  );
}
